import React, { createContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Create a context for theme
export const ThemeContext = createContext({
  isDarkMode: true,
  toggleTheme: () => {},
});

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Get user preference from localStorage or default to dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode !== null ? JSON.parse(savedMode) : true; // Default to dark mode
  });

  // Toggle theme function
  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  // Create theme based on current mode
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        primary: {
          main: '#ff5b8d', // Pink accent color from the images
        },
        secondary: {
          main: '#7952f6', // Purple for secondary elements
        },
        background: {
          default: isDarkMode ? '#0f0a1e' : '#f5f5f5', // Matches the main background
          paper: isDarkMode ? '#1a1429' : '#ffffff', // Matches drawer and app bar
        },
        info: {
          main: '#5be5ff', // Cyan for info
        },
        success: {
          main: '#4caf50', // Green for success
        },
        warning: {
          main: '#ff9800', // Orange for warnings
        },
        error: {
          main: '#f44336', // Red for errors
        },
        text: {
          primary: isDarkMode ? '#ffffff' : '#333333', // White text in dark mode
          secondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', // Lighter secondary text
        },
      },
      typography: {
        fontFamily: [
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(','),
        h1: {
          fontSize: '2.5rem',
          fontWeight: 700,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        h2: {
          fontSize: '2rem',
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        h3: {
          fontSize: '1.75rem',
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        h4: {
          fontSize: '1.5rem',
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        h5: {
          fontSize: '1.25rem',
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        h6: {
          fontSize: '1rem',
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        body1: {
          fontSize: '1rem',
          color: isDarkMode ? '#ffffff' : '#333333',
        },
        body2: {
          fontSize: '0.875rem',
          color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
        },
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? '#1a1429' : '#ffffff',
              boxShadow: isDarkMode 
                ? '0 4px 6px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              color: isDarkMode ? '#ffffff' : '#333333',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: isDarkMode ? '#1a1429' : '#ffffff',
              borderRight: 'none',
              color: isDarkMode ? '#ffffff' : '#333333',
              width: 240, // Fixed width to match the images
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? 'rgba(26, 20, 41, 0.8)' : '#ffffff',
              borderRadius: '8px',
              boxShadow: isDarkMode 
                ? '0 4px 8px rgba(0, 0, 0, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              color: isDarkMode ? '#ffffff' : '#333333',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              color: isDarkMode ? '#ffffff' : '#333333',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 91, 141, 0.1)' : 'rgba(255, 91, 141, 0.2)',
              },
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: isDarkMode ? 'rgba(255, 91, 141, 0.2)' : 'rgba(255, 91, 141, 0.1)',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 91, 141, 0.1)' : 'rgba(255, 91, 141, 0.05)',
              },
              color: isDarkMode ? '#ffffff' : '#333333',
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: isDarkMode ? '#1a1429' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#333333',
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
    });
  }, [isDarkMode]);

  // Provide the theme context to children components
  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};